import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import { Box, Divider, IconButton, Stack } from '@mui/material'
import { OrganizationBySlugMatch } from '@stytch/vanilla-js'
import pixydocsHorizDark from '@/assets/pixydocs-horiz-dark.svg'
import { EMAIL_REGEX } from '@/utils/auth'
import { FormTextField } from '@/components/forms'
import LoginWithMagicLink from './LoginWithMagicLink'
import LoginWithPassword from './LoginWithPassword'
import LoginWithSSO from './LoginWithSSO'

export type LoginViewOptions = {
  stytchOrg?: OrganizationBySlugMatch
  email: string
  resetView: boolean
  setEmail: Dispatch<SetStateAction<string>>
  setShowOnly: Dispatch<
    SetStateAction<'all' | 'magic_link' | 'password' | 'sso'>
  >
}

type LoginProps = {
  stytchOrg?: OrganizationBySlugMatch
}

type EmailProps = {
  email: string
  setEmail: Dispatch<SetStateAction<string>>
}

export function Email({ email, setEmail }: EmailProps) {
  const { setValue } = useFormContext()

  useEffect(() => {
    setValue('email', email, { shouldDirty: true, shouldValidate: true })
  }, [email, setValue])

  return (
    <FormTextField
      name="email"
      autoFocus
      fullWidth
      variant="outlined"
      label="Email"
      size="medium"
      required
      onChange={(e) => {
        setEmail(e.target.value)
      }}
      useControllerProps={{
        rules: {
          validate: (value) => EMAIL_REGEX.test(value),
        },
      }}
    />
  )
}

const allowedMethods = ['magic_link', 'password', 'sso']

export default function Login({ stytchOrg }: LoginProps) {
  const [showOnly, setShowOnly] = useState<
    'all' | 'magic_link' | 'password' | 'sso'
  >('all')
  const [email, setEmail] = useState<string>('')

  const allowedAuthMethods =
    stytchOrg?.auth_methods !== 'ALL_ALLOWED' &&
    stytchOrg?.allowed_auth_methods?.length
      ? stytchOrg.allowed_auth_methods
      : allowedMethods

  const commonProps = {
    stytchOrg,
    email,
    resetView: showOnly === 'all',
    setEmail,
    setShowOnly,
  }

  return (
    <Stack spacing={6} alignItems="center">
      <Box sx={{ width: '100%', position: 'relative', textAlign: 'center' }}>
        {showOnly !== 'all' && (
          <IconButton
            sx={{ position: 'absolute', top: 8, left: -12 }}
            onClick={() => {
              setShowOnly('all')
            }}
          >
            <KeyboardArrowLeft />
          </IconButton>
        )}
        <img
          src={pixydocsHorizDark}
          alt="Pixydocs Logo"
          style={{ width: 'auto', height: '50px' }}
        />
      </Box>

      {allowedAuthMethods.includes('magic_link') &&
        (showOnly === 'all' || showOnly === 'magic_link') && (
          <LoginWithMagicLink {...commonProps} />
        )}

      <Stack spacing={1} sx={{ width: '100%' }}>
        {allowedAuthMethods.includes('password') &&
          (showOnly === 'all' || showOnly === 'password') && (
            <LoginWithPassword {...commonProps} />
          )}
        {allowedAuthMethods.includes('sso') && (
          <>
            {showOnly === 'all' && allowedAuthMethods.length > 1 && (
              <Divider flexItem sx={{ pb: 0.75 }}>
                <span style={{ lineHeight: 1 }}>or</span>
              </Divider>
            )}
            {(showOnly === 'all' || showOnly === 'sso') && (
              <LoginWithSSO {...commonProps} />
            )}
          </>
        )}
      </Stack>
    </Stack>
  )
}
